import React, { useState, useEffect, useCallback } from "react";
import { downscaleImage } from "../lib/image";

function ScanStocksModal({ onClose, onScan, onBulkAdd }) {
  const [stage, setStage] = useState("pick"); // pick | scanning | review
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      setStatus(null);
      try {
        const { base64, mimeType, preview: dataUrl } = await downscaleImage(file);
        setPreview(dataUrl);
        setStage("scanning");

        const data = await onScan(base64, mimeType);
        const found = data.stocks || [];

        if (found.length === 0) {
          setStage("pick");
          setPreview(null);
          setStatus({ type: "error", message: "לא זוהו מניות בתמונה" });
          return;
        }

        setRows(
          found.map((s, i) => ({
            key: i,
            symbol: s.symbol || "",
            name: s.name || "",
            purchase_price: s.purchase_price ?? "",
            quantity: s.quantity ?? "",
          }))
        );
        setStage("review");
      } catch (err) {
        setStage("pick");
        setPreview(null);
        setStatus({ type: "error", message: err.message });
      }
    },
    [onScan]
  );

  // Ctrl+V of a screenshot is likely the most common real-world path.
  useEffect(() => {
    if (stage !== "pick") return;
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find((i) =>
        i.type.startsWith("image/")
      );
      if (item) handleFile(item.getAsFile());
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [stage, handleFile]);

  const updateRow = (key, field, value) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const missingSymbols = rows.filter((r) => !r.symbol.trim()).length;

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const result = await onBulkAdd(
        rows.map((r) => ({
          symbol: r.symbol.trim(),
          name: r.name.trim() || null,
          purchase_price: r.purchase_price === "" ? null : r.purchase_price,
          quantity: r.quantity === "" ? null : r.quantity,
        }))
      );

      const parts = [`נוספו ${result.inserted} מניות`];
      if (result.skipped?.length) parts.push(`${result.skipped.length} כבר היו ברשימה`);
      if (result.invalid?.length) parts.push(`${result.invalid.length} סימולים לא תקינים`);
      setStatus({ type: "success", message: parts.join(" · ") });

      setTimeout(onClose, 1400);
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          &times;
        </button>
        <div className="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>

        {stage === "pick" && (
          <>
            <h2>סריקת תמונה</h2>
            <p className="modal-desc">
              העלה צילום מסך של התיק שלך ו-AI יחלץ ממנו את המניות. אפשר גם להדביק תמונה עם Ctrl+V.
            </p>

            <label
              className={`scan-dropzone${dragging ? " dragover" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleFile(e.target.files?.[0])}
                hidden
              />
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>גרור לכאן תמונה או לחץ לבחירה</span>
              <span className="scan-hint">JPG · PNG · WEBP</span>
            </label>
          </>
        )}

        {stage === "scanning" && (
          <>
            <h2>סורק את התמונה…</h2>
            <p className="modal-desc">זה עשוי לקחת עד חצי דקה</p>
            {preview && <img className="scan-preview" src={preview} alt="" />}
            <div className="loader-container">
              <div className="loader" />
            </div>
          </>
        )}

        {stage === "review" && (
          <>
            <h2>אישור המניות שזוהו</h2>
            <p className="modal-desc">
              בדוק ותקן לפני השמירה. שדות ריקים יישמרו ריקים וניתן למלא אותם אחר כך.
            </p>

            <div className="scan-review-list">
              {rows.map((r) => (
                <div className={`scan-review-row${!r.symbol.trim() ? " warn" : ""}`} key={r.key}>
                  <input
                    className="field-input"
                    dir="ltr"
                    placeholder="סימול"
                    value={r.symbol}
                    onChange={(e) => updateRow(r.key, "symbol", e.target.value.toUpperCase())}
                  />
                  <input
                    className="field-input"
                    dir="auto"
                    placeholder="שם"
                    value={r.name}
                    onChange={(e) => updateRow(r.key, "name", e.target.value)}
                  />
                  <input
                    className="field-input"
                    dir="ltr"
                    type="number"
                    step="any"
                    min="0"
                    placeholder="מחיר"
                    value={r.purchase_price}
                    onChange={(e) => updateRow(r.key, "purchase_price", e.target.value)}
                  />
                  <input
                    className="field-input"
                    dir="ltr"
                    type="number"
                    step="any"
                    min="0"
                    placeholder="כמות"
                    value={r.quantity}
                    onChange={(e) => updateRow(r.key, "quantity", e.target.value)}
                  />
                  <button className="icon-btn danger" onClick={() => removeRow(r.key)} title="הסר">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {missingSymbols > 0 && (
              <p className="scan-hint warn-text">
                יש {missingSymbols} שורות ללא סימול — מלא אותן או הסר אותן כדי לשמור
              </p>
            )}

            <div className="btn-row">
              <button className="btn-secondary" onClick={onClose} disabled={saving}>
                ביטול
              </button>
              <button
                className="modal-submit"
                onClick={save}
                disabled={saving || rows.length === 0 || missingSymbols > 0}
              >
                {saving ? "שומר..." : `שמור ${rows.length} מניות`}
              </button>
            </div>
          </>
        )}

        {status && <div className={`modal-status ${status.type}`}>{status.message}</div>}
      </div>
    </div>
  );
}

export default ScanStocksModal;
